import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { MapPin, Users, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import RatingStars from "../components/RatingStars";
import MapView from "../components/MapView";
import { useAuth } from "../contexts/AuthContext";

export default function VenueDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [venue, setVenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(1);
  const [booking, setBooking] = useState(false);

  useEffect(() => {
    api.get(`/venues/${id}`).then((r) => setVenue(r.data)).catch(() => toast.error("Lieu introuvable")).finally(() => setLoading(false));
  }, [id]);

  const reserve = async () => {
    if (!user) { nav("/login"); return; }
    setBooking(true);
    try {
      const { data } = await api.post("/checkout/session", {
        item_type: "venue", item_id: venue.id, quantity: 1, extra_days: days,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
      setBooking(false);
    }
  };

  if (loading) return <div className="p-12"><Loader2 className="animate-spin" /></div>;
  if (!venue) return null;
  const img = venue.images?.[0] || "https://images.unsplash.com/photo-1772903191730-fa9bc478c1de?w=1600";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="aspect-video rounded-md overflow-hidden mb-8">
        <img src={img} alt={venue.name} className="w-full h-full object-cover" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <p className="text-label text-muted-foreground mb-3">Lieu événementiel</p>
          <h1 data-testid="venue-title" className="font-heading font-light tracking-editorial text-4xl lg:text-5xl text-ink mb-4 leading-tight">{venue.name}</h1>
          {venue.rating_avg > 0 && <RatingStars rating={venue.rating_avg} count={venue.rating_count} />}
          <div className="flex flex-wrap gap-4 my-6 text-sm">
            <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-accent" /> {venue.address}, {venue.city}</span>
            <span className="flex items-center gap-2"><Users className="h-4 w-4 text-accent" /> {venue.capacity} personnes</span>
          </div>
          <p className="text-muted-foreground leading-relaxed mb-8">{venue.description}</p>

          <h2 className="font-heading font-medium text-xl text-ink mb-4 tracking-tight">Équipements</h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {venue.equipment?.map((eq) => (
              <li key={eq} className="flex items-center gap-2 text-sm">
                <CheckCircle2 className="h-4 w-4 text-trust" /> {eq}
              </li>
            ))}
          </ul>

          {/* Mini-map */}
          {typeof venue.lat === "number" && typeof venue.lng === "number" && (
            <div className="mt-10" data-testid="venue-mini-map">
              <h2 className="font-heading font-medium text-xl text-ink mb-4 tracking-tight">Emplacement</h2>
              <div className="h-72">
                <MapView
                  items={[{
                    id: venue.id, name: venue.name, city: venue.city,
                    lat: venue.lat, lng: venue.lng, price_per_day: venue.price_per_day,
                  }]}
                  type="venue"
                  height="100%"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {venue.address} · {venue.city}
              </p>
            </div>
          )}
        </div>

        <aside className="lg:sticky lg:top-24 self-start">
          <div className="bg-white border border-ink/10 rounded-md p-6">
            <div className="flex items-baseline gap-1 mb-1">
              <span className="font-heading font-black text-3xl text-ink">{venue.price_per_day}€</span>
              <span className="text-sm text-muted-foreground">/ jour</span>
            </div>
            <p className="text-xs text-muted-foreground mb-6">Commission plateforme 20% incluse</p>

            <div className="mb-4">
              <Label htmlFor="days">Nombre de jours</Label>
              <Input id="days" type="number" min={1} value={days} onChange={(e) => setDays(parseInt(e.target.value) || 1)} data-testid="venue-days" />
            </div>

            <div className="border-t border-ink/10 pt-4 mb-4 space-y-2 text-sm">
              <div className="flex justify-between"><span>{venue.price_per_day}€ × {days}j</span><span>{venue.price_per_day * days}€</span></div>
              <div className="flex justify-between font-bold text-primary border-t border-ink/10 pt-2"><span>Total</span><span>{(venue.price_per_day * days).toFixed(2)}€</span></div>
            </div>

            <Button data-testid="venue-reserve" onClick={reserve} disabled={booking} className="w-full bg-ink hover:bg-ink/85 text-cream rounded-none">
              {booking ? <Loader2 className="h-4 w-4 animate-spin" /> : "Réserver ce lieu"}
            </Button>
          </div>
        </aside>
      </div>
    </div>
  );
}
